/**
 * 새 티켓을 만든다. **모달 안에서 돈다**(2026-08-26 지시).
 *
 * 옛 판은 칸 안 인라인 폼이었다 — 칸 폭이 240px 이라 「자세히」를 펴면
 * 라디오가 두 줄씩 접히며 **카드 여섯 장 높이**가 됐다(실측). 모달은 폭이
 * 넉넉해 선택지가 한 줄에 선다.
 *
 * **제목만으로 만들 수 있다.** 나머지는 접혀 있고 펴야 나온다 — 이슈를
 * 내는 사람에게 타입·우선순위·담당자를 먼저 묻는 순간 접수창구가 무거워진다
 * (비개발 둘이 쓰는 입구가 그것뿐이다).
 */

import { useState, type ClipboardEvent, type FormEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';

import type {
  Label,
  Project,
  TemplateSection,
  TicketCol,
  TicketDevice,
  TicketEnvironment,
  TicketKind,
  TicketPriority,
  TicketType,
  UserBrief,
} from '../api';
import { ROLE_LABEL, errorMessage } from '../api';
import { imageFilesFromDataTransfer, uploadAndInsert } from '../attachments';
import {
  DEVICES,
  ENVIRONMENTS,
  KINDS,
  kindLabel,
  PRIORITIES,
  TYPES,
} from '../columns';
import { Markdown } from '../ui/Markdown';
import { ModalActions } from '../ui/ModalActions';
import { Check, ChoiceGroup, Radio } from '../ui/Choice';
import { AssigneeMark, LabelChip, PriorityMark, TypeMark } from './Chips';
import { renderTemplate, templateHint } from './ticketTemplates';

/*
  **양식은 서버가 준다**(2026-09-16). 옛 판은 이슈 양식 하나를 여기 상수로
  들고 있었는데, 챗봇이 세우는 초안도 같은 절 이름을 써야 해서 정본을
  서버(`app/ticket_templates.py`)로 옮겼다 — `GET /tickets/meta` 의
  `templates` 가 유형마다 하나씩 준다. 화면에 한 벌 더 적으면 두 입구의
  절 이름이 갈린다.

  ⚠ **디바이스·환경은 양식에 없다.** 이슈에는 그 둘이 이미 칸으로 있다
  (2026-08-26 티켓 요소 확정) — 양식에 또 적으면 같은 것을 두 곳에 쓰게
  되고, 둘이 어긋났을 때 어느 쪽이 맞는지 알 길이 없다.

  **빈 절은 서버가 만들 때 걷는다.** 양식을 넣고 한 절만 채워도 빈 제목이
  본문에 안 남는다.
*/

/**
 * 챗봇이 세운 초안 — **폼의 시작값이다.** 사람이 보고 고친 뒤 「만들기」를
 * 눌러야 티켓이 된다(2026-09-02 확정 「초안은 티켓이 아니다」).
 *
 * 프로젝트·칸·작업자·라벨은 여기 없다 — 모델이 정할 것이 아니라 화면의
 * 값을 쓴다.
 */
export type DraftSeed = {
  title: string;
  body: string;
  priority: TicketPriority | null;
  device: TicketDevice | null;
  environment: TicketEnvironment | null;
  due_date: string | null;
};

export type CreateInput = {
  /** 어느 프로젝트에 낼까. **기본은 지금 보고 있는 것**이다. */
  project_id: number;
  /**
   * 어느 칸에 설까.
   *
   * **폼이 정한다**(2026-08-29 지시로 칸 머리의 `+` 를 걷었다). 옛 판은
   * 부르는 쪽이 칸을 박아 넘겼다 — 만드는 자리가 칸마다 하나씩 있었기
   * 때문인데, 공통 단추 하나가 되면서 그 정보가 사라졌다.
   */
  col: TicketCol;
  title: string;
  body: string;
  /** **만들 때만 정한다.** 그 뒤로 바꿀 길이 없다. */
  kind: TicketKind;
  device: TicketDevice | null;
  environment: TicketEnvironment | null;
  type: TicketType | null;
  priority: TicketPriority | null;
  assignee_id: number | null;
  /** 마감. `YYYY-MM-DD`. 서버는 전부터 받았는데 폼이 안 보내고 있었다(2026-09-16). */
  due_date: string | null;
  label_ids: number[];
};

export function CreateForm({
  defaultCol,
  projects,
  defaultProjectId,
  busy,
  members,
  labels,
  templates,
  locked,
  initial,
  note,
  onCancel,
  onSubmit,
}: {
  /**
   * 처음에 서 있을 칸. 유형을 바꾸면 여기에 딸려 간다 —
   * 이슈는 「이슈」 칸, 할 일은 「Todo」 칸이 접수함이다.
   */
  defaultCol: TicketCol;
  projects: Project[];
  /** 지금 보고 있는 프로젝트. 여기에 내는 것이 거의 언제나 맞다. */
  defaultProjectId: number;
  busy: boolean;
  members: UserBrief[];
  labels: Label[];
  /** 유형별 본문 양식. 서버가 준다 — 아직 안 왔으면 「양식 넣기」를 안 그린다. */
  templates: Record<TicketKind, TemplateSection[]> | null;
  /**
   * 대화 앞단계에서 **이미 고른 것**. 있으면 프로젝트·유형·상태를 안 그린다.
   *
   * ⚠ **상태는 유형이 정한다**(2026-09-16 사용자 지정) — 접수하는 사람에게
   * 「어느 칸에 둘까요」는 대답할 수 없는 물음이다. 잠긴 경로에서는 그 칸을
   * 아예 안 보여 준다.
   */
  locked?: { projectId: number; kind: TicketKind } | null;
  /** 챗봇이 세운 초안. 있으면 그 값으로 시작하고 「자세히」를 펴 둔다. */
  initial?: DraftSeed | null;
  /** 제목 위에 얹을 한 줄 — 「AI 초안이다. 확인하고 만들어라」 같은 것. */
  note?: ReactNode;
  onCancel: () => void;
  onSubmit: (input: CreateInput) => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [projectId, setProjectId] = useState(locked?.projectId ?? defaultProjectId);
  const [kind, setKind] = useState<TicketKind>(
    locked?.kind ?? (defaultCol === 'issue' ? 'issue' : 'todo'),
  );
  /**
   * 어느 칸에 설까. **유형이 정한다**(2026-09-16 사용자 지정: 「유형이 이슈면
   * 상태도 이슈, 할 일이면 Todo로 가야 하니까 유형만 남겨라」).
   *
   * ⚠ **고르게 두지 마라.** 옛 판은 라디오 넷을 세워 두었는데(2026-08-29),
   * 접수하는 사람에게 「이걸 어느 칸에 둘까요」는 대답할 수 없는 물음이고
   * 둘이 어긋나면(유형은 이슈인데 칸은 완료) 보드가 거짓말을 한다.
   * 이미 하고 있는 일은 만든 뒤 카드를 끌어다 놓는다.
   */
  const col: TicketCol = kind === 'issue' ? 'issue' : 'todo';
  const [device, setDevice] = useState<TicketDevice | null>(initial?.device ?? null);
  const [environment, setEnvironment] = useState<TicketEnvironment | null>(
    initial?.environment ?? null,
  );
  const [type, setType] = useState<TicketType | null>(null);
  const [priority, setPriority] = useState<TicketPriority | null>(initial?.priority ?? null);
  const [assignee, setAssignee] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState<string>(initial?.due_date ?? '');
  const [labelIds, setLabelIds] = useState<number[]>([]);
  // **초안이 있으면 펴 둔다.** 초안의 본문을 접어 두면 사람이 안 보고 만든다 —
  // 그러면 「확인하고 만들기」가 거짓이 된다.
  const [more, setMore] = useState(initial != null);
  const sections = templates?.[kind] ?? null;
  // 스크린샷 붙여넣기(첨부 팀, 2026-09-01). QA 가 버그를 글로만 못 적는
  // 사람이라 — 스크린샷이 텔레그램에 남으면 히스토리가 반쪽이 된다.
  const [pasting, setPasting] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  /** 설명을 그려서 보나(2026-10-18 UI 회의). **AI 초안이면 그려 둔 채로
      연다** — 만들기 전에 상세에서 보일 모양 그대로 검토하게 한다. */
  const [preview, setPreview] = useState(Boolean(initial?.body));

  async function handleBodyPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const files = imageFilesFromDataTransfer(event.clipboardData);
    // 글자 붙여넣기는 브라우저 기본 동작 그대로 둔다.
    if (files.length === 0) return;
    event.preventDefault();
    // 자리(커서)는 await 전에 잡는다 — 응답이 오는 사이 포커스가 떠날 수 있다.
    const box = event.currentTarget;
    const start = box.selectionStart ?? box.value.length;
    const end = box.selectionEnd ?? start;
    setPasting(true);
    setPasteError(null);
    try {
      const fragments: string[] = [];
      for (const file of files) fragments.push(await uploadAndInsert(file));
      const inserted = fragments.join('\n');
      setBody((prev) => {
        // 올리는 사이 글자가 바뀌었을 수 있다 — 자리를 범위 안으로 죈다.
        const from = Math.min(start, prev.length);
        const to = Math.min(Math.max(end, from), prev.length);
        return prev.slice(0, from) + inserted + prev.slice(to);
      });
    } catch (err) {
      setPasteError(errorMessage(err));
    } finally {
      setPasting(false);
    }
  }


  /**
   * 양식을 본문에 넣는다. **비어 있을 때만** — 쓴 글을 덮지 않는다.
   *
   * 단추도 비어 있을 때만 눌리지만 판정을 여기 한 번 더 둔다. `setBody` 의
   * 갱신 함수 안이라 **누르는 순간의 값**을 본다 — 밖에서 `body` 로 재면
   * 붙여넣기가 올리는 중에 들어온 글자를 놓칠 수 있다.
   */
  function insertTemplate() {
    if (!sections) return;
    const text = renderTemplate(sections);
    setBody((prev) => (prev.trim() ? prev : text));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed || busy) return;
    onSubmit({
      project_id: projectId,
      col,
      title: trimmed,
      body: body.trim(),
      kind,
      // 유형을 바꿔 놓고 접었다 펴면 옛 값이 남을 수 있다.
      // **할 일에는 디바이스·환경을 안 보낸다** — 서버가 400 으로 거절한다.
      device: kind === 'issue' ? device : null,
      environment: kind === 'issue' ? environment : null,
      type,
      priority,
      assignee_id: assignee,
      due_date: dueDate || null,
      label_ids: labelIds,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
      {/* **머리와 동작 줄은 붙박이고 가운데만 스크롤된다**(2026-09-16).
          「자세히」를 펴면 속성이 여덟 줄까지 서는데, 옛 판은 모달 전체가
          늘어나 제목과 「만들기」가 화면 밖으로 밀렸다. */}
      <header className="flex shrink-0 items-center border-b border-line px-s5 py-s3">
        <h2 className="m-0 text-lead font-bold text-ink">새 티켓</h2>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-s5 py-s4">
      {note && (
        <p role="status" className="mb-s3 rounded-md bg-brand-t px-s3 py-s2 text-meta text-brand-d">
          {note}
        </p>
      )}
      <input
        autoFocus
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder="무엇을 해야 하나"
        aria-label="제목"
        className="w-full rounded-md border border-line bg-panel px-s3 py-s2 text-lead text-ink outline-none placeholder:text-ink-3 focus-visible:border-brand max-[720px]:min-h-[44px]"
      />

      {/*
        **프로젝트가 둘 이상일 때만 고르게 한다**(2026-08-26 지시). 하나면
        고를 것이 없고, 그 자리에 선택기가 서 있으면 「다른 데도 낼 수 있나」를
        묻게 만든다. 기본은 지금 보고 있는 프로젝트다.
      */}
      {!locked && projects.length > 1 && (
        <dl className="mt-s4 grid grid-cols-[68px_1fr] items-start gap-x-s4 gap-y-s3">
          <Field label="프로젝트">
          <ChoiceGroup label="프로젝트">
            {projects.map((item) => (
              <Radio
                key={item.id}
                name="create-project"
                label={item.name}
                nullable={false}
                checked={projectId === item.id}
                onChange={() => setProjectId(item.id)}
              >
                {item.name}
              </Radio>
            ))}
          </ChoiceGroup>
          </Field>
        </dl>
      )}

      {/*
        **유형은 접힌 자리에 두지 않는다**(2026-08-26). 만들 때만 정하고 그
        뒤로 바꿀 길이 없어서, 「자세히」 안에 숨기면 대부분 기본값(할 일)로
        만들어진 뒤에야 이슈였다는 것을 알게 된다.
      */}
      {/* ⚠ **이름표를 지우지 마라.** 프로젝트 줄이 위에 붙으면 라디오 두 줄이
          나란히 서는데, 이름이 없으면 아래 줄이 무엇인지 알 수 없다(실측). */}
      {locked ? (
        /* 앞단계에서 고른 것을 **읽기로만** 되짚는다. 여기서 고치게 두면
           초안의 본문 양식(절 이름)이 유형과 어긋난다. */
        <p className="m-0 mt-s2 text-meta text-ink-3">
          {projects.find((p) => p.id === locked.projectId)?.name ?? ''} · {kindLabel(locked.kind)}
        </p>
      ) : (
      <dl className="mt-s3 grid grid-cols-[68px_1fr] items-start gap-x-s4 gap-y-s3">
        <Field label="유형">
        <ChoiceGroup label="유형">
          {KINDS.map((item) => (
            <Radio
              key={item.id}
              name="create-kind"
              label={item.label}
              nullable={false}
              checked={kind === item.id}
              onChange={() => setKind(item.id)}
            >
              {item.label}
            </Radio>
          ))}
        </ChoiceGroup>
        </Field>

      </dl>
      )}

      {more && (
        <dl className="mt-s4 grid grid-cols-[68px_1fr] items-start gap-x-s4 gap-y-s3 border-t border-line pt-s4">
          <Field label="설명">
            {preview && body.trim() !== '' ? (
              <div
                className="max-h-[320px] overflow-y-auto rounded-md border border-line bg-panel px-s3 py-s2"
                aria-label="설명 미리보기"
              >
                <Markdown>{body}</Markdown>
              </div>
            ) : (
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              onPaste={(event) => {
                void handleBodyPaste(event);
              }}
              placeholder={sections ? '없어도 된다 — 「양식 넣기」가 무엇을 적을지 알려 준다' : '없어도 된다'}
              aria-label="설명"
              rows={3}
              className="w-full resize-y rounded-md border border-line bg-panel px-s3 py-s2 text-meta text-ink max-[720px]:text-lead"
            />
            )}
            <div className="mt-s1 flex flex-wrap items-center gap-x-s3 gap-y-s1">
              {/* 붙여넣기가 있다는 것을 알리는 한 줄. 올리는 동안·실패도 이 자리다. */}
              <p className={`m-0 text-micro max-[720px]:basis-full ${pasteError ? 'text-st-red-f' : 'text-ink-3'}`}>
                {pasting
                  ? '스크린샷 올리는 중…'
                  : (pasteError ?? '스크린샷은 여기에 붙여넣으면 첨부된다')}
              </p>
              {/*
                **양식은 누를 때만 들어간다**(자동으로 안 채운다).

                이 설명칸은 「자세히」 안에 접혀 있고, 이 폼의 원칙은 **제목
                만으로 만들 수 있다**는 것이다. 저절로 채우면 칸을 한 번도
                펴지 않은 사람의 티켓이 **빈 양식만 든 본문**으로 만들어진다 —
                「안 된다」 한 줄보다 나쁘다. 읽는 쪽이 열어 봐야 아무것도 안
                적혔다는 것을 알게 되기 때문이다. 유형을 할 일로 바꿨다
                되돌리는 동안 남은 글자를 사람이 지워야 하는 것도 같은 이유다.

                **관문이 아니다.** 지운 채로도, 채우다 만 채로도 서버는 받는다.
              */}
              {body.trim() !== '' && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="ml-auto text-ink-3"
                  aria-pressed={preview}
                  onClick={() => setPreview((v) => !v)}
                >
                  {preview ? '고쳐 쓰기' : '미리보기'}
                </Button>
              )}
              {sections && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className={`${body.trim() !== '' ? '' : 'ml-auto'} text-ink-3`}
                  title={templateHint(sections)}
                  // 쓴 글을 덮지 않는다 — 비어 있을 때만 눌린다.
                  disabled={body.trim() !== ''}
                  onClick={insertTemplate}
                >
                  양식 넣기
                </Button>
              )}
            </div>
          </Field>
          {/*
            **마감은 여기다.** 서버는 `due_date` 를 전부터 받았는데 폼이 안
            보내고 있었다(2026-09-16 발견) — 상세에서만 고칠 수 있었다.
          */}
          <Field label="마감">
            <input
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              aria-label="마감"
              className="rounded-md border border-line bg-panel px-s3 py-s1 text-meta text-ink max-[720px]:min-h-[44px] max-[720px]:text-lead"
            />
          </Field>
          {/* 디바이스·환경은 **이슈에만** 있다 — 할 일에는 「어디서 났나」가 없다. */}
          {kind === 'issue' && (
            <>
              <Field label="디바이스">
                <ChoiceGroup label="디바이스">
                  {DEVICES.map((item) => (
                    <Radio
                      key={item.id}
                      name="create-device"
                      label={item.label}
                      checked={device === item.id}
                      onChange={(on) => setDevice(on ? item.id : null)}
                    >
                      {item.label}
                    </Radio>
                  ))}
                </ChoiceGroup>
              </Field>

              <Field label="환경">
                <ChoiceGroup label="환경">
                  {ENVIRONMENTS.map((item) => (
                    <Radio
                      key={item.id}
                      name="create-environment"
                      label={item.label}
                      checked={environment === item.id}
                      onChange={(on) => setEnvironment(on ? item.id : null)}
                    >
                      {item.label}
                    </Radio>
                  ))}
                </ChoiceGroup>
              </Field>
            </>
          )}

          {/*
            **고르는 것은 전부 라디오·체크박스다**(2026-08-26 지시). 옛 판은
            선택기 셋이 나란히 있었는데, 티켓을 내는 사람이 그 각각을 열어
            봐야 무엇을 고를 수 있는지 알 수 있었다. 펼쳐 두면 한 번에 고른다.

            `name` 에 `create-` 를 붙인다 — 상세 모달이 같은 화면에 함께 뜰 수
            있고, `name` 이 겹치면 **두 곳의 라디오가 한 묶음이 된다.**
          */}
          <Field label="작업 유형">
            <ChoiceGroup label="타입">
              {TYPES.map((item) => (
                <Radio
                  key={item.id}
                  name="create-type"
                  label={item.label}
                  checked={type === item.id}
                  onChange={(on) => setType(on ? item.id : null)}
                >
                  {/* 배지가 곧 이름이다 — 글자를 또 적지 않는다. */}
                  <TypeMark type={item.id} />
                </Radio>
              ))}
            </ChoiceGroup>
          </Field>

          <Field label="우선순위">
            <ChoiceGroup label="우선순위">
              {PRIORITIES.map((item) => (
                <Radio
                  key={item.id}
                  name="create-priority"
                  label={item.label}
                  checked={priority === item.id}
                  onChange={(on) => setPriority(on ? item.id : null)}
                >
                  <PriorityMark priority={item.id} />
                </Radio>
              ))}
            </ChoiceGroup>
          </Field>

          {members.length > 0 && (
            <Field label="작업자">
              <ChoiceGroup label="담당자">
                {members.map((item) => (
                  <Radio
                    key={item.id}
                    name="create-assignee"
                    label={item.display_name}
                    checked={assignee === item.id}
                    onChange={(on) => setAssignee(on ? item.id : null)}
                  >
                    <AssigneeMark user={item} size={18} />
                    {item.display_name}
                    <span className="ml-s1 text-micro text-ink-3">{ROLE_LABEL[item.role]}</span>
                  </Radio>
                ))}
              </ChoiceGroup>
            </Field>
          )}

          {labels.length > 0 && (
            <Field label="라벨">
              <ChoiceGroup label="라벨">
                {labels.map((label) => {
                  const on = labelIds.includes(label.id);
                  return (
                    <Check
                      key={label.id}
                      label={label.name}
                      checked={on}
                      onChange={(next) =>
                        setLabelIds((prev) =>
                          next ? [...prev, label.id] : prev.filter((id) => id !== label.id),
                        )
                      }
                    >
                      <LabelChip label={label} muted={!on} />
                    </Check>
                  );
                })}
              </ChoiceGroup>
            </Field>
          )}
        </dl>
      )}

      {/* **긍정은 오른쪽 끝이다**(2026-09-16). 배치의 주인은 `ModalActions` —
          옛 판은 「만들기」가 맨 왼쪽이었다. */}
      </div>

      <ModalActions
        placement="frame"
        secondary={[
          {
            label: more ? '접기' : '자세히',
            expanded: more,
            onClick: () => setMore((prev) => !prev),
          },
        ]}
        cancel={{ label: '취소', onClick: onCancel }}
        primary={{
          label: busy ? '만드는 중…' : '만들기',
          type: 'submit',
          disabled: busy || !title.trim(),
        }}
      />
    </form>
  );
}

/**
 * 「자세히」 안의 한 줄. **이름표를 왼쪽에 둔다**(2026-08-26 지시 — 소제목
 * 금지). 티켓 상세와 같은 격자라 만들 때와 고칠 때가 같은 모양이다.
 *
 * 옛 판은 이름을 위에 작게 얹었다. 선택지가 가로로 퍼지니 위가 자연스러워
 * 보였지만, 그 결과 **작은 글자가 층층이 쌓여** 폼이 목록처럼 읽혔다.
 */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="pt-s2 text-meta text-ink-3">{label}</dt>
      <dd className="m-0 min-w-0">{children}</dd>
    </>
  );
}
